import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import { AesEncrypt, AesDecrypt, keyPromise } from '../Service/crypto';

/**
 * Loads and saves the visible columns a user chose for one table.
 * savedColumns is null until the user customises the table (null = show every column).
 */
export const useColumnPreferences = (tableName: string) => {
  const [savedColumns, setSavedColumns] = useState<string[] | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const mounted = useRef<boolean>(true);

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      try {
        const key = await keyPromise;
        const EncParams = await AesEncrypt(key, JSON.stringify({ table_name: tableName }));
        const response = await api.fetch('api/ColumnPreference/get', {
          method: 'POST',
          body: JSON.stringify({ EncParams }),
        });
        if (!response.ok) throw new Error(`Column preference load failed (${response.status})`);
        const decrypted = await AesDecrypt(key, await response.json());
        if (mounted.current) {
          setSavedColumns(Array.isArray(decrypted?.columns) ? decrypted.columns : null);
        }
      } catch (error) {
        console.error('Error loading column preferences:', error);
      } finally {
        if (mounted.current) setLoaded(true);
      }
    };
    load();
    return () => {
      mounted.current = false;
    };
  }, [tableName]);

  const saveColumns = useCallback(
    async (columns: string[] | null) => {
      setSavedColumns(columns);
      try {
        const key = await keyPromise;
        const EncParams = await AesEncrypt(key, JSON.stringify({ table_name: tableName, columns }));
        const response = await api.fetch('api/ColumnPreference/save', {
          method: 'POST',
          body: JSON.stringify({ EncParams }),
        });
        if (!response.ok) throw new Error(`Column preference save failed (${response.status})`);
      } catch (error) {
        console.error('Error saving column preferences:', error);
      }
    },
    [tableName]
  );

  return { savedColumns, loaded, saveColumns };
};

export default useColumnPreferences;
